/* AgentGoalGrid — 4 tiles showing what's READY for you (deliverables),
   not in-flight counts. Reframes "agent busywork" as "your inbox." */

import { useNavigate } from 'react-router-dom';
import { FileSearch, FileCheck2, Mail, Mic, ArrowRight } from 'lucide-react';
import type { JobPosting } from '@/modules/job/services/job.api';
import type { TaskItem } from '@/modules/shared/types/yc-demo';

export interface AgentGoalGridProps {
    jobs: JobPosting[];
    tasks: TaskItem[];
    surface?: string;
    text?: string;
    muted?: string;
    rule?: string;
}

function hash(s: string) {
    return s.split('').reduce((a, c) => (a * 31 + c.charCodeAt(0)) & 0xffff, 7);
}

export function AgentGoalGrid({
    jobs,
    tasks,
    surface = 'rgba(255,255,255,0.65)',
    text = '#2a241e',
    muted = '#9c948a',
    rule = 'rgba(120, 90, 50, 0.08)',
}: AgentGoalGridProps) {
    const navigate = useNavigate();
    const seed = jobs[0] ? hash(jobs[0]._id) : 17;
    const cv = tasks.filter((t) => t.kind === 'cv_review').length;
    const intReviews = tasks.filter((t) => t.kind === 'ai_interview_review').length;

    const tiles = [
        {
            label: 'Sourced & ready',
            sub: 'passed my bar',
            value: 5 + (seed % 4),
            cta: 'Open',
            href: '/v2/jobs',
            tint: 'rgb(0 132 255)',
            bg: 'rgba(0, 132, 255, 0.08)',
            Icon: FileSearch,
        },
        {
            label: 'CV reviews',
            sub: 'awaiting your call',
            value: cv,
            cta: 'Review',
            href: '/v2/jobs',
            tint: 'rgb(20 143 63)',
            bg: 'rgba(86, 218, 139, 0.12)',
            Icon: FileCheck2,
        },
        {
            label: 'Outreach drafts',
            sub: 'ready to send',
            value: 8 + (seed % 6),
            cta: 'Approve',
            href: '/v2/jobs',
            tint: 'rgb(192 88 11)',
            bg: 'rgba(239, 154, 91, 0.14)',
            Icon: Mail,
        },
        {
            label: 'Interview reports',
            sub: 'fresh transcripts',
            value: intReviews,
            cta: 'Read',
            href: '/v2/jobs',
            tint: 'rgb(108 70 145)',
            bg: 'rgba(167, 124, 198, 0.14)',
            Icon: Mic,
        },
    ];

    return (
        <div
            style={{
                background: surface,
                border: `1px solid ${rule}`,
                borderRadius: 18,
                padding: 18,
                fontFamily: 'var(--v2-font-sans)',
            }}
        >
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'baseline',
                    marginBottom: 12,
                }}
            >
                <span style={{ fontSize: 13, fontWeight: 600, color: text, letterSpacing: '-0.01em' }}>
                    Ready for you
                </span>
                <span
                    style={{
                        fontSize: 10,
                        color: muted,
                        textTransform: 'uppercase',
                        letterSpacing: '0.06em',
                        fontWeight: 600,
                    }}
                >
                    delivered by agents
                </span>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                {tiles.map((t) => {
                    const Icon = t.Icon;
                    const empty = t.value === 0;
                    return (
                        <button
                            key={t.label}
                            type="button"
                            onClick={() => navigate(t.href)}
                            disabled={empty}
                            style={{
                                background: empty ? 'rgba(120, 90, 50, 0.04)' : t.bg,
                                borderRadius: 12,
                                padding: '12px 14px',
                                border: 'none',
                                cursor: empty ? 'default' : 'pointer',
                                textAlign: 'left',
                                color: text,
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 6,
                                fontFamily: 'inherit',
                                transition: 'transform 140ms ease',
                                opacity: empty ? 0.55 : 1,
                            }}
                            onMouseEnter={(e) => !empty && (e.currentTarget.style.transform = 'translateY(-1px)')}
                            onMouseLeave={(e) => (e.currentTarget.style.transform = 'translateY(0)')}
                        >
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: t.tint }}>
                                <Icon size={12} strokeWidth={2} />
                                <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: '-0.01em' }}>
                                    {t.label}
                                </span>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
                                <span
                                    style={{
                                        fontFamily: 'var(--v2-font-serif)',
                                        fontSize: 28,
                                        fontWeight: 400,
                                        color: text,
                                        letterSpacing: '-0.02em',
                                        lineHeight: 1,
                                    }}
                                >
                                    {t.value}
                                </span>
                                <span style={{ fontSize: 11, color: muted }}>{t.sub}</span>
                            </div>
                            {!empty && (
                                <div
                                    style={{
                                        marginTop: 2,
                                        fontSize: 11,
                                        color: t.tint,
                                        fontWeight: 600,
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: 4,
                                    }}
                                >
                                    {t.cta}
                                    <ArrowRight size={10} strokeWidth={2.5} />
                                </div>
                            )}
                        </button>
                    );
                })}
            </div>
        </div>
    );
}
